import type { OrganizationSettings } from '@threadheads/shared';

export interface BrandingSource {
  id: string;
  name?: string | null;
  slug?: string | null;
  settings?: OrganizationSettings | null;
}

export interface ResolvedBranding {
  displayName: string;
  /** Sidebar header logo (dark surface) — a light-coloured logo. */
  logoUrl: string;
  /**
   * App loading screen logo (light/white surface) — a dark-coloured logo.
   * Resolves from the org's own light-surface upload (branding.logoLightUrl),
   * then falls back to its sidebar upload (branding.logoUrl), then the
   * ThreadHeads dark wordmark. The legacy /org-logos/{slug}.png files are
   * sidebar-only assets and never leak here — pre-branding the loading screen
   * showed the wordmark for every org, so no-upload orgs must keep exactly that.
   */
  loadingLogoUrl: string;
  /** Browser-tab favicon. Custom upload, else the Blue Monk default. */
  faviconUrl: string;
  mascotUrl: string | null;
  isDefault: boolean;
}

// Platform DEFAULT branding = PrintRun (the product, by Blue Monk). Every real org — including
// Threadheads, whose own branding is seeded in
// 20260715120000_seed_threadheads_branding_logos.sql — carries its own; only the
// truly null-branded / pre-org state falls back here.
//   logoUrl        → sidebar, a DARK surface  → the LIGHT (white) wordmark.
//   loadingLogoUrl → loading + auth screens, a WHITE surface → the DARK (navy) wordmark.
export const DEFAULT_BRANDING: ResolvedBranding = {
  displayName: 'PrintRun',
  logoUrl: '/brand/printrun-logo-white.png',
  loadingLogoUrl: '/brand/printrun-logo.png',
  faviconUrl: '/brand/favicon-32.png',
  mascotUrl: null,
  isDefault: true,
};

/**
 * Resolves an org record to its rendered branding.
 *
 * Sidebar logo (dark surface): custom upload (settings.branding.logoUrl) ->
 * legacy per-slug PNG (/org-logos/${slug}.png) -> the ThreadHeads default.
 *
 * Loading-screen logo (white surface): the org's own light-surface upload
 * (settings.branding.logoLightUrl) -> its sidebar upload (logoUrl) -> the
 * ThreadHeads default. The logoUrl step keeps single-logo orgs unchanged; the
 * legacy per-slug PNG is deliberately NOT in this chain (see loadingLogoUrl).
 *
 * Display name falls back through the org's own name to "ThreadHeads".
 * `isDefault` is only true when there's no org at all (pre-load) — an org
 * with no custom branding still resolves to real (legacy-fallback) values,
 * not "default".
 */
export function resolveBranding(org: BrandingSource | null): ResolvedBranding {
  if (!org) {
    return DEFAULT_BRANDING;
  }
  const branding = org.settings?.branding;
  const legacyLogoUrl = org.slug ? `/org-logos/${org.slug}.png` : null;
  return {
    displayName: branding?.displayName || org.name || DEFAULT_BRANDING.displayName,
    logoUrl: branding?.logoUrl || legacyLogoUrl || DEFAULT_BRANDING.logoUrl,
    loadingLogoUrl: branding?.logoLightUrl || branding?.logoUrl || DEFAULT_BRANDING.loadingLogoUrl,
    faviconUrl: branding?.faviconUrl || DEFAULT_BRANDING.faviconUrl,
    mascotUrl: branding?.mascotUrl ?? null,
    isDefault: false,
  };
}

const BRANDING_CACHE_KEY = 'brandingCache:v1';

interface CachedBranding {
  orgId: string;
  displayName: string;
  logoUrl: string;
  loadingLogoUrl: string;
  faviconUrl: string;
  mascotUrl: string | null;
  cachedAt: number;
}

function getActiveOrgId(): string | null {
  try {
    return localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || null;
  } catch {
    return null;
  }
}

/**
 * Reads the last-cached branding synchronously, keyed to the CURRENTLY active
 * org (localStorage activeOrgId/selected_org_id) — used by AppLoadingScreen
 * so the very first paint shows the right logo instead of flashing
 * ThreadHeads then swapping once OrganizationContext resolves.
 *
 * Three states, not two, because "no cache" and "cache is for a different
 * org" need different renders:
 * - `null` = no cache entry at all (first-ever load on this device, or
 *   storage unavailable) — caller should render the ThreadHeads default.
 * - `'mismatch'` = a cache entry exists but for a different org than the one
 *   about to load (org switch in flight, or stale from a prior session) —
 *   caller should render NEITHER logo, since both would be wrong for a beat.
 * - `ResolvedBranding` = a cache entry for the active org.
 */
export function readBrandingCache(): ResolvedBranding | 'mismatch' | null {
  const activeOrgId = getActiveOrgId();
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedBranding>;
    if (
      typeof parsed.orgId !== 'string' ||
      typeof parsed.displayName !== 'string' ||
      typeof parsed.logoUrl !== 'string' ||
      typeof parsed.loadingLogoUrl !== 'string' ||
      (parsed.mascotUrl !== null && typeof parsed.mascotUrl !== 'string')
    ) {
      return null;
    }
    if (parsed.orgId !== activeOrgId) {
      return 'mismatch';
    }
    return {
      displayName: parsed.displayName,
      logoUrl: parsed.logoUrl,
      loadingLogoUrl: parsed.loadingLogoUrl,
      // Lenient: a pre-favicon cache entry (no faviconUrl) stays valid and just
      // uses the Blue Monk default until the next writeBrandingCache refreshes it.
      faviconUrl: typeof parsed.faviconUrl === 'string' ? parsed.faviconUrl : DEFAULT_BRANDING.faviconUrl,
      mascotUrl: parsed.mascotUrl ?? null,
      isDefault: false,
    };
  } catch {
    return null;
  }
}

export function writeBrandingCache(orgId: string, branding: ResolvedBranding): void {
  try {
    const payload: CachedBranding = {
      orgId,
      displayName: branding.displayName,
      logoUrl: branding.logoUrl,
      loadingLogoUrl: branding.loadingLogoUrl,
      faviconUrl: branding.faviconUrl,
      mascotUrl: branding.mascotUrl,
      cachedAt: Date.now(),
    };
    localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Private-mode Safari / storage full — best-effort cache only, never blocks app load.
  }
}

export function clearBrandingCache(): void {
  try {
    localStorage.removeItem(BRANDING_CACHE_KEY);
  } catch {
    // ignore
  }
}
